import { createColumnHelper } from '@tanstack/react-table';
import { CombinedMmmModelOutput } from './models/mmm-model-output';
import { CurrencyDisplayWrapper } from 'features/currency/currencyDisplayWrapper';

const columnHelper = createColumnHelper<CombinedMmmModelOutput>();

const currencyCode = 'GBP';
const format0 = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});
const format1 = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 2
});

const isTotalRow = (row: CombinedMmmModelOutput) => {
  return row.segmentValue === 'Total';
};

export const getMmmOutputColumnDefs = (
  primaryModelName = 'Primary',
  comparedModelName = 'Compared',
  isCompareMode = false
) => {
  const groupedMetric = (
    idPrefix: string,
    label: string,
    primaryKey: keyof CombinedMmmModelOutput,
    comparedKey: keyof CombinedMmmModelOutput,
    isEditingButtonAllowed: boolean,
    formatter: (val: number) => string | JSX.Element
  ) => ({
    header: label,
    id: idPrefix,
    columns: [
      columnHelper.accessor(primaryKey, {
        id: `${idPrefix}-primary`,
        header: primaryModelName,
        size: 100,
        enableColumnFilter: false,
        cell: (info) => {
          const value = info.getValue();
          const styleClasses = `text-left ${
            isTotalRow(info.row.original) ? 'font-bold' : ''
          }`;

          return (
            <div className={styleClasses}>
              {value != null && !isNaN(Number(value))
                ? formatter(Number(value))
                : '-'}
            </div>
          );
        }
      }),
      columnHelper.accessor(comparedKey, {
        id: `${idPrefix}-compared`,
        header: comparedModelName,
        size: 160,
        enableColumnFilter: false,
        cell: (info) => {
          const value = info.getValue();
          const styleClasses = `text-left flex  gap-2 ${
            isTotalRow(info.row.original) ? 'font-bold' : ''
          }`;

          return (
            <div className={styleClasses}>
              {value != null && !isNaN(Number(value))
                ? formatter(Number(value))
                : '-'}
              {isEditingButtonAllowed && info.row.getCanExpand() && (
                <button
                  className="text-sm text-blue-500"
                  onClick={() => info.row.toggleExpanded()}
                >
                  {info.row.getIsExpanded() ? (
                    <i className="ra-icons-generic-cross-1" />
                  ) : (
                    <i className="ra-icons-generic-edit" />
                  )}
                </button>
              )}
            </div>
          );
        }
      }),
      columnHelper.display({
        id: `${idPrefix}-diff`,
        header: '% Diff',
        size: 80,
        enableColumnFilter: false,
        cell: (info) => {
          const row = info.row.original;
          const primary = row[primaryKey];
          const compared = row[comparedKey];

          if (
            typeof primary !== 'number' ||
            typeof compared !== 'number' ||
            primary === 0 ||
            row.segmentValue === 'Total'
          ) {
            return <div className="text-center text-gray-400">-</div>;
          }

          const diff = ((compared - primary) / primary) * 100;
          const color = diff > 0 ? 'text-success-400' : 'text-error-300';
          const formatted = `${diff > 0 ? '+' : ''}${format1.format(diff)}%`;

          return (
            <div className={`text-left font-medium ${color}`}>{formatted}</div>
          );
        }
      })
    ]
  });

  const columns: any[] = [
    columnHelper.accessor((row) => row.segmentValue, {
      id: 'channel',
      header: 'Channel',
      size: 200,
      enableColumnFilter: false,
      cell: (info) => {
        const styleClasses = `text-left ${
          isTotalRow(info.row.original) ? 'font-bold' : ''
        }`;
        return <span className={styleClasses}>{info.getValue() || '-'}</span>;
      }
    })
  ];

  if (isCompareMode) {
    columns.push(
      groupedMetric(
        'spend',
        'Spend',
        'spendPrimary',
        'spendCompared',
        true,
        (val) => (
          <CurrencyDisplayWrapper currencyCode={currencyCode}>
            {format0.format(val)}
          </CurrencyDisplayWrapper>
        )
      ),
      groupedMetric(
        'revenue',
        'Revenue',
        'revenuePrimary',
        'revenueCompared',
        false,
        (val) => (
          <CurrencyDisplayWrapper currencyCode={currencyCode}>
            {format0.format(val)}
          </CurrencyDisplayWrapper>
        )
      ),
      groupedMetric(
        'roas',
        'ROAS',
        'roasPrimary',
        'roasCompared',
        false,
        (val) => format1.format(val)
      ),
      groupedMetric(
        'mroas',
        'mROAS',
        'mROASPrimary',
        'mROASCompared',
        false,
        (val) => format1.format(val)
      )
    );
  } else {
    columns.push(
      columnHelper.accessor('spendPrimary', {
        id: 'spend',
        header: 'Spend',
        size: 100,
        cell: (info) => {
          const value = info.getValue();
          const styleClasses = `text-left ${
            isTotalRow(info.row.original) ? 'font-bold' : ''
          }`;
          return value != null && !isNaN(Number(value)) ? (
            <CurrencyDisplayWrapper
              currencyCode={currencyCode}
              className={styleClasses}
            >
              {format0.format(Number(value))}
            </CurrencyDisplayWrapper>
          ) : (
            '-'
          );
        }
      }),
      columnHelper.accessor('revenuePrimary', {
        id: 'revenue',
        header: 'Revenue',
        size: 100,
        cell: (info) => {
          const value = info.getValue();
          const styleClasses = `text-left ${
            isTotalRow(info.row.original) ? 'font-bold' : ''
          }`;
          return value != null && !isNaN(Number(value)) ? (
            <>
              <CurrencyDisplayWrapper
                currencyCode={currencyCode}
                className={styleClasses}
              >
                {format0.format(Number(value))}
              </CurrencyDisplayWrapper>
            </>
          ) : (
            '-'
          );
        }
      }),
      columnHelper.accessor('roasPrimary', {
        id: 'roas',
        header: 'ROAS',
        size: 80,
        cell: (info) => {
          const value = info.getValue();
          const styleClasses = `text-left ${
            isTotalRow(info.row.original) ? 'font-bold' : ''
          }`;
          return value != null && !isNaN(Number(value)) ? (
            <span className={styleClasses}>
              {format1.format(Number(value))}
            </span>
          ) : (
            '-'
          );
        }
      }),
      columnHelper.accessor('mROASPrimary', {
        id: 'mroas',
        header: 'mROAS',
        size: 80,
        cell: (info) => {
          const value = info.getValue();
          const styleClasses = `text-left ${
            isTotalRow(info.row.original) ? 'font-bold' : ''
          }`;
          return value != null && !isNaN(Number(value)) ? (
            <span className={styleClasses}>
              {format1.format(Number(value))}
            </span>
          ) : (
            '-'
          );
        }
      })
    );
  }

  return columns;
};
